// Admin hook for full boutique/business management with Firestore
import { useState, useEffect } from 'react';
import {
  collection,
  query,
  onSnapshot,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  GeoPoint,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';

// Full admin boutique type — includes all fields from Firestore
export interface AdminBoutique {
  id: string;
  name: string;
  description: string;
  logo: string;
  coverImage: string;
  featuredImage?: string;
  featuredOrder?: number;
  address: string | { street: string; city: string; state: string; zipCode: string; unit?: string };
  location?: GeoPoint;
  rating: number;
  reviewCount: number;
  categories: string[];
  collections?: string[];
  hours: {
    [key: string]: { open: string; close: string } | null;
  };
  deliveryRadius: number;
  active: boolean;
  featured: boolean;
  socialMedia?: {
    instagram?: string;
    tiktok?: string;
    website?: string;
  };
  priceRange?: { min: number; max: number };
  commissionOverride?: number; // per-boutique commission override (%)
  adminNotes?: string;         // internal admin notes
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

// What admin can update on a boutique
export type BoutiqueUpdate = Partial<
  Omit<AdminBoutique, 'id' | 'rating' | 'reviewCount' | 'createdAt'>
>;

// Audit log entry
export interface AdminAuditLog {
  id?: string;
  boutiqueId: string;
  boutiqueName: string;
  action: string;
  changes: Record<string, { from: any; to: any }>;
  adminNote?: string;
  createdAt: Timestamp;
}

// ─── Fetch ALL boutiques for admin (no active filter) ─────────────
export function useAdminBoutiques() {
  const [boutiques, setBoutiques] = useState<AdminBoutique[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'businesses'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as AdminBoutique[];
        setBoutiques(data);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching boutiques for admin:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return { boutiques, loading, error };
}

// ─── Fetch single boutique for admin ──────────────────────────────
export function useAdminBoutique(boutiqueId: string | null) {
  const [boutique, setBoutique] = useState<AdminBoutique | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!boutiqueId) {
      setBoutique(null);
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'businesses', boutiqueId),
      (snap) => {
        if (snap.exists()) {
          setBoutique({ id: snap.id, ...snap.data() } as AdminBoutique);
        } else {
          setBoutique(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching boutique:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [boutiqueId]);

  return { boutique, loading, error };
}

// ─── Write helpers ────────────────────────────────────────────────

// Log an admin action to the admin_logs collection
async function logAdminAction(log: Omit<AdminAuditLog, 'id' | 'createdAt'>) {
  try {
    await addDoc(collection(db, 'admin_logs'), {
      ...log,
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.error('Failed to write audit log:', err);
    // Don't throw — audit log failure shouldn't block the action
  }
}

// Build a diff of what changed
function buildChanges(
  current: Record<string, any>,
  updates: Record<string, any>
): Record<string, { from: any; to: any }> {
  const changes: Record<string, { from: any; to: any }> = {};
  for (const key of Object.keys(updates)) {
    const from = current[key];
    const to = updates[key];
    // Simple comparison — good enough for primitive & shallow object fields
    if (JSON.stringify(from) !== JSON.stringify(to)) {
      changes[key] = { from, to };
    }
  }
  return changes;
}

// Update any fields on a boutique + log it
export async function updateBoutique(
  boutique: AdminBoutique,
  updates: BoutiqueUpdate,
  adminNote?: string
): Promise<void> {
  const ref = doc(db, 'businesses', boutique.id);

  // Featured carousel fields must go through the admin callable.
  const { featured: _f, featuredOrder: _o, featuredImage: _i, ...safeUpdates } = updates as BoutiqueUpdate & {
    featured?: boolean;
    featuredOrder?: number;
    featuredImage?: string | null;
  };

  const changes = buildChanges(boutique as any, safeUpdates as any);
  if (Object.keys(changes).length === 0) return;

  await updateDoc(ref, {
    ...safeUpdates,
    updatedAt: serverTimestamp(),
  });

  await logAdminAction({
    boutiqueId: boutique.id,
    boutiqueName: boutique.name,
    action: 'update',
    changes,
    adminNote,
  });
}

// Toggle featured flag (admin callable — server-side audit log)
export async function toggleFeatured(boutique: AdminBoutique): Promise<void> {
  const adminUpdate = httpsCallable(functions, 'adminUpdateBusinessFeatured');
  await adminUpdate({
    businessId: boutique.id,
    featured: !boutique.featured,
  });
}

/** Update featured carousel order (admin only). */
export async function updateFeaturedOrder(
  boutique: AdminBoutique,
  featuredOrder: number,
  adminNote?: string,
): Promise<void> {
  const adminUpdate = httpsCallable(functions, 'adminUpdateBusinessFeatured');
  await adminUpdate({
    businessId: boutique.id,
    featuredOrder,
    adminNote,
  });
}

/** Set or clear the featured hero image override (admin only). */
export async function updateFeaturedImage(
  boutique: AdminBoutique,
  featuredImage: string | null,
  adminNote?: string,
): Promise<void> {
  const adminUpdate = httpsCallable(functions, 'adminUpdateBusinessFeatured');
  await adminUpdate({
    businessId: boutique.id,
    featuredImage,
    adminNote,
  });
}

// Change boutique status (active / suspended)
export async function setBoutiqueStatus(
  boutique: AdminBoutique,
  active: boolean,
  reason?: string
): Promise<void> {
  const ref = doc(db, 'businesses', boutique.id);

  await updateDoc(ref, {
    active,
    updatedAt: serverTimestamp(),
  });

  await logAdminAction({
    boutiqueId: boutique.id,
    boutiqueName: boutique.name,
    action: active ? 'activate' : 'suspend',
    changes: { active: { from: boutique.active, to: active } },
    adminNote: reason,
  });
}

// Permanently delete a boutique and its products directly via Firestore
export async function deleteBoutique(
  boutique: AdminBoutique,
  reason?: string,
  _deleteOwnerAccount: boolean = true
): Promise<{ deleted: { business: boolean; products: number } }> {
  const {
    collection: col,
    query: q,
    where,
    getDocs,
    writeBatch,
  } = await import('firebase/firestore');

  const batch = writeBatch(db);
  let productsRemoved = 0;

  // 1. Find and batch-delete all products for this business
  const productsSnap = await getDocs(
    q(col(db, 'products'), where('brandId', '==', boutique.id))
  );
  productsSnap.docs.forEach((d) => {
    batch.delete(d.ref);
    productsRemoved++;
  });

  // 2. Delete the business document itself
  batch.delete(doc(db, 'businesses', boutique.id));

  await batch.commit();

  // 3. Write an audit log
  await logAdminAction({
    boutiqueId: boutique.id,
    boutiqueName: boutique.name,
    action: 'delete_business',
    changes: {
      status: { from: boutique.active ? 'active' : 'suspended', to: 'deleted' },
      productsRemoved: { from: productsRemoved, to: 0 },
    },
    adminNote: reason,
  });

  return { deleted: { business: true, products: productsRemoved } };
}

// ─── Fetch audit logs for a boutique ──────────────────────────────
export function useAdminLogs(boutiqueId: string | null) {
  const [logs, setLogs] = useState<AdminAuditLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!boutiqueId) {
      setLogs([]);
      setLoading(false);
      return;
    }

    const q = query(collection(db, 'admin_logs'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const all = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() } as AdminAuditLog))
        .filter((l) => l.boutiqueId === boutiqueId)
        .sort((a, b) => {
          const aTime = a.createdAt?.seconds ?? 0;
          const bTime = b.createdAt?.seconds ?? 0;
          return bTime - aTime;
        });
      setLogs(all);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [boutiqueId]);

  return { logs, loading };
}
